import React from "react";
import { QRCodeSVG } from "qrcode.react";
import logo from "assets/images/logo/logo.png";
import { foamDensityWithTolerance, ltrValue, notchLabel } from "Common/constants/panelSpec";
import {
  numberToPersianWords,
  printAmount,
  printDecimal,
  printSheetThickness,
  saleTermsUrl,
} from "Common/constants/invoicePrint";

// ========== نوع پاسخ GET /proforma-invoices/{id}/print-data ==========
// همه‌ی داده‌ی چاپ از همین یک درخواست می‌آید؛ اطلاعات فروشنده، توضیحات و شرایط فروش
// دیگر در فرانت‌اند ثابت نیستند.

export interface PrintSurface {
  material?: string | null;
  thickness?: number | string | null;
  color?: string | null;
}

export interface PrintItem {
  id?: number;
  line_number?: number | null;
  goods_code?: string | null;
  goods_title?: string | null;
  goods_description?: string | null;
  goods_full_description?: string | null;
  panel_type?: string | null;
  panel_thickness?: number | string | null;
  outer_surface?: PrintSurface | null;
  inner_surface?: PrintSurface | null;
  foam_type?: string | null;
  foam_density?: number | string | null;
  /** «نک‌دار رو» و «نک‌دار زیر»؛ جایگزین فیلد منسوخ has_notch. */
  has_notch_top?: boolean | null;
  has_notch_bottom?: boolean | null;
  /** منسوخ؛ فقط برای رکوردهای قدیمی خوانده می‌شود. */
  has_notch?: boolean | null;
  quantity?: number | string | null;
  length?: string | number | null;
  width?: string | number | null;
  area?: string | number | null;
  /**
   * مقادیر آماده‌ی نمایش با دقیقاً دو رقم اعشار. سرور آن‌ها را می‌سازد و فرانت‌اند
   * هیچ محاسبه‌ای روی‌شان انجام نمی‌دهد؛ length/width عددی فقط برای محاسبات‌اند.
   */
  length_display?: string | null;
  width_display?: string | null;
  area_display?: string | null;
  price_per_square_meter?: string | number | null;
  total_rial?: string | number | null;
  packaging_cost?: string | number | null;
  vat_amount?: string | number | null;
  final_amount?: string | number | null;
}

export interface PrintTotals {
  total_area?: string | number | null;
  total_area_display?: string | null;
  roof_area?: string | number | null;
  roof_area_display?: string | null;
  wall_area?: string | number | null;
  wall_area_display?: string | null;
  grand_total?: string | number | null;
  packaging_cost?: string | number | null;
  vat_amount?: string | number | null;
  final_amount?: string | number | null;
}

export interface PrintInvoice {
  id?: number;
  invoice_number?: number | string | null;
  invoice_date?: string | null;
  /**
   * ساعت ثبت پیش‌فاکتور در منطقه زمانی تهران.
   */
  invoice_time?: string | null;
  valid_until?: string | null;
  order_status?: string | null;
  sale_type?: string | null;
  currency_id?: number | null;
  currency_name?: string | null;
  customer_id?: number | null;
  customer_name?: string | null;
  sales_expert_id?: number | null;
  sales_expert_username?: string | null;
  sales_expert_fullname?: string | null;
  items?: PrintItem[] | null;
  totals?: PrintTotals | null;
}

export interface PrintBuyer {
  id?: number;
  customer_type?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  national_code?: string | null;
  national_id?: string | null;
  economic_code?: string | null;
  phone_number?: string | null;
  province_name?: string | null;
  city_name?: string | null;
  postal_code?: string | null;
  address?: string | null;
}

export interface PrintBankAccount {
  bank_name?: string | null;
  iban?: string | null;
}

export interface PrintSeller {
  name?: string | null;
  national_id?: string | null;
  economic_code?: string | null;
  phone_number?: string | null;
  postal_code?: string | null;
  address?: string | null;
  bank_accounts?: PrintBankAccount[] | null;
}

export interface PrintDescription {
  id: number;
  description: string;
}

export interface PrintTermsSection {
  title?: string | null;
  items?: string[] | null;
}

export interface PrintTermsContact {
  messenger_phone?: string | null;
  office_address?: string | null;
  factory_address?: string | null;
  phone_numbers?: string[] | null;
  website?: string | null;
}

export interface PrintSalesTerms {
  source_url?: string | null;
  snapshot_date?: string | null;
  title?: string | null;
  sections?: PrintTermsSection[] | null;
  contact?: PrintTermsContact | null;
  plain_text?: string | null;
}

export interface ProformaPrintData {
  invoice: PrintInvoice;
  buyer?: PrintBuyer | null;
  seller?: PrintSeller | null;
  descriptions?: PrintDescription[] | null;
  sales_terms?: PrintSalesTerms | null;
}

export interface ProformaPrintDocumentProps {
  data: ProformaPrintData;
}

const cell = "border border-black px-1 py-1 text-center align-middle";
const headCell = `${cell} font-bold bg-[#d9d9d9]`;

/** مقدار خالی در چاپ با «-» نشان داده می‌شود، نه رشته‌ی خالی. */
const show = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

/**
 * قالب چاپ پیش‌فاکتور.
 *
 * همه‌ی مقادیر مالی و متراژ از پاسخ سرور می‌آیند و اینجا دوباره محاسبه نمی‌شوند تا
 * چاپ با اطلاعات ثبت‌شده‌ی پیش‌فاکتور اختلاف نداشته باشد؛ فقط قالب‌بندی نمایشی
 * (جداکننده‌ی هزارگان و تبدیل به حروف) انجام می‌شود.
 *
 * روی صفحه دیده نمی‌شود و فقط هنگام چاپ ظاهر می‌شود (نگاه کنید به custom.scss).
 */
const ProformaPrintDocument = ({ data }: ProformaPrintDocumentProps) => {
  const invoice = data?.invoice || {};
  const buyer = data?.buyer || null;
  const seller = data?.seller || null;
  const descriptions = data?.descriptions || [];
  const terms = data?.sales_terms || null;

  const items = invoice.items || [];
  const totals = invoice.totals || {};

  const buyerName = [buyer?.first_name, buyer?.last_name].filter(Boolean).join(" ");
  // برای شخص حقیقی کد ملی و برای حقوقی شناسه ملی نمایش داده می‌شود.
  const buyerNationalId =
    buyer?.customer_type === "legal"
      ? buyer?.national_id
      : buyer?.national_code || buyer?.national_id;
  const buyerAddress = [buyer?.province_name, buyer?.city_name, buyer?.address]
    .filter(Boolean)
    .join("، ");

  return (
    <div className="proforma-print" dir="rtl">
      {/* ===== سربرگ ===== */}
      <table className="w-full border-collapse border border-black">
        <tbody>
          <tr>
            <td className="w-1/3 p-2 align-middle border border-black">
              <img src={logo} alt="ماهان پانل" className="inline-block h-10 align-middle" />
              <span className="inline-block mr-2 text-xl font-bold align-middle">
                ماهان پانل
              </span>
            </td>
            <td className="w-1/3 p-2 text-lg font-bold text-center border border-black">
              پیش فاکتور
            </td>
            <td className="w-1/3 p-2 align-middle border border-black">
              <table className="w-full">
                <tbody>
                  <tr>
                    <td className="text-right">شماره :</td>
                    <td className="font-bold text-left">{show(invoice.invoice_number)}</td>
                  </tr>
                  <tr>
                    <td className="text-right">تاریخ / ساعت :</td>
                    <td className="font-bold text-left">
                      {invoice.invoice_time ? `${ltrValue(String(invoice.invoice_time))} - ` : ""}{ltrValue(show(invoice.invoice_date))}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right">کارشناس فروش :</td>
                    <td className="font-bold text-left">
                      {show(invoice.sales_expert_fullname || invoice.sales_expert_username)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ===== فروشنده ===== */}
      <table className="w-full mt-1 border-collapse border border-black">
        <tbody>
          <tr>
            <td className="p-1 border border-black w-[55%]">
              <b>فروشنده :</b> {show(seller?.name)}
            </td>
            <td className="p-1 border border-black">
              <b>شناسه ملی/کد ملی :</b> {show(seller?.national_id)}
            </td>
          </tr>
          <tr>
            <td className="p-1 border border-black">
              <b>شماره تماس :</b> {show(seller?.phone_number)}
            </td>
            <td className="p-1 border border-black">
              <b>کد اقتصادی :</b> {show(seller?.economic_code)}
            </td>
          </tr>
          <tr>
            <td className="p-1 border border-black">
              <b>آدرس :</b> {show(seller?.address)}
            </td>
            <td className="p-1 border border-black">
              <b>کد پستی :</b> {show(seller?.postal_code)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ===== خریدار ===== */}
      <table className="w-full mt-1 border-collapse border border-black">
        <tbody>
          <tr>
            <td className="p-1 border border-black w-[55%]">
              <b>خریدار :</b> {buyerName || show(invoice.customer_name)}
            </td>
            <td className="p-1 border border-black">
              <b>شناسه ملی /کد ملی:</b> {show(buyerNationalId)}
            </td>
          </tr>
          <tr>
            <td className="p-1 border border-black">
              <b>شماره تماس :</b> {show(buyer?.phone_number)}
            </td>
            <td className="p-1 border border-black">
              <b>کد اقتصادی :</b> {show(buyer?.economic_code)}
            </td>
          </tr>
          <tr>
            <td className="p-1 border border-black">
              <b>آدرس :</b> {buyerAddress || "-"}
            </td>
            <td className="p-1 border border-black">
              <b>کد پستی :</b> {show(buyer?.postal_code)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ===== اقلام ===== */}
      <table className="w-full mt-1 text-[10px] border-collapse border border-black">
        <thead>
          <tr>
            <th className={headCell} rowSpan={2}>ردیف</th>
            {/* «نک‌دار» از نسخه ۳ به دو ستون «رو» و «زیر» تقسیم شده، پس colSpan یکی زیاد شد. */}
            <th className={headCell} colSpan={4}>پانل</th>
            <th className={headCell} colSpan={4}>روکش</th>
            <th className={headCell} colSpan={2}>فوم</th>
            <th className={headCell} colSpan={6}>متراژ و بهای فروش</th>
          </tr>
          <tr>
            <th className={headCell}>شرح کالا</th>
            <th className={headCell}>ضخامت (Cm)</th>
            <th className={headCell}>نک‌دار رو</th>
            <th className={headCell}>نک‌دار زیر</th>
            <th className={headCell}>سطح</th>
            <th className={headCell}>نوع ورق</th>
            <th className={headCell}>ضخامت(mm)</th>
            <th className={headCell}>رنگ ورق</th>
            <th className={headCell}>نوع فوم</th>
            <th className={headCell}>دانسیته (Kg M3)</th>
            <th className={headCell}>تعداد</th>
            <th className={headCell}>طول</th>
            <th className={headCell}>عرض</th>
            <th className={headCell}>متراژ (m2)</th>
            <th className={headCell}>قیمت واحد متر مربع</th>
            <th className={headCell}>مبلغ کل ریال</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            // هر قلم دو سطر دارد: سطح بیرونی و سطح داخلی. بقیه‌ی ستون‌ها ادغام می‌شوند.
            <React.Fragment key={item.id ?? index}>
              <tr>
                <td className={cell} rowSpan={2}>{item.line_number ?? index + 1}</td>
                <td className={cell} rowSpan={2}>
                  <div>{show(item.goods_code)}</div>
                  <div>{item.goods_full_description || item.goods_description || item.goods_title || ""}</div>
                </td>
                <td className={cell} rowSpan={2}>{show(item.panel_thickness)}</td>
                {/* رکوردهای قدیمی فقط has_notch دارند؛ همان جایگزین هر دو می‌شود. */}
                <td className={cell} rowSpan={2}>
                  {notchLabel(item.has_notch_top ?? item.has_notch)}
                </td>
                <td className={cell} rowSpan={2}>
                  {notchLabel(item.has_notch_bottom ?? item.has_notch)}
                </td>

                <td className={cell}>بیرونی</td>
                <td className={cell}>{show(item.outer_surface?.material)}</td>
                <td className={cell}>{printSheetThickness(item.outer_surface?.thickness)}</td>
                <td className={cell}>{show(item.outer_surface?.color)}</td>

                <td className={cell} rowSpan={2}>{show(item.foam_type)}</td>
                <td className={cell} rowSpan={2}>
                  {foamDensityWithTolerance(item.foam_density)}
                </td>
                <td className={cell} rowSpan={2}>{show(item.quantity)}</td>
                {/* طول و عرض از مقادیر آماده‌ی سرور خوانده می‌شوند که همیشه دو رقم اعشار
                    دارند؛ فقط اگر سرور نفرستاده باشد، در فرانت‌اند قالب‌بندی می‌شود. */}
                <td className={cell} rowSpan={2}>
                  {item.length_display || printDecimal(item.length, 2)}
                </td>
                <td className={cell} rowSpan={2}>
                  {item.width_display || printDecimal(item.width, 2)}
                </td>
                {/* متراژ مستقیم از سرور؛ اینجا محاسبه نمی‌شود. */}
                <td className={cell} rowSpan={2}>{show(item.area_display)}</td>
                <td className={cell} rowSpan={2}>
                  {printAmount(item.price_per_square_meter)}
                </td>
                <td className={cell} rowSpan={2}>{printAmount(item.total_rial)}</td>
              </tr>
              <tr>
                <td className={cell}>داخلی</td>
                <td className={cell}>{show(item.inner_surface?.material)}</td>
                <td className={cell}>{printSheetThickness(item.inner_surface?.thickness)}</td>
                <td className={cell}>{show(item.inner_surface?.color)}</td>
              </tr>
            </React.Fragment>
          ))}

          {/* ===== سطر «جمع متراژ» زیر ستون متراژ ===== */}
          {/* ستون‌های پیش از «متراژ» در یک سلول ادغام می‌شوند تا عدد دقیقاً زیر همان
              ستون بنشیند: ردیف + ۴ ستون پانل + ۴ ستون روکش + ۲ ستون فوم +
              تعداد و طول و عرض = ۱۴ ستون. */}
          <tr>
            <td className={`${cell} bg-[#d9d9d9] font-bold text-left`} colSpan={14}>
              جمع متراژ
            </td>
            <td className={`${cell} bg-[#d9d9d9] font-bold`}>
              {totals.total_area_display || printDecimal(totals.total_area, 2)}
            </td>
            {/* دو ستون آخر (قیمت واحد و مبلغ کل) در این سطر خالی می‌مانند. */}
            <td className={`${cell} bg-[#d9d9d9]`} colSpan={2}></td>
          </tr>
        </tbody>
      </table>

      <table className="w-full mt-1 border-collapse border border-black text-[11px]">
        <tbody>
          <tr>
            <td className={`${cell} bg-[#d9d9d9] font-bold`}>جمع متراژ سقفی</td>
            <td className={`${cell} font-bold`}>{totals.roof_area_display ?? printDecimal(totals.roof_area, 2)}</td>
            <td className={`${cell} bg-[#d9d9d9] font-bold`}>جمع متراژ دیواری</td>
            <td className={`${cell} font-bold`}>{totals.wall_area_display ?? printDecimal(totals.wall_area, 2)}</td>
          </tr>
        </tbody>
      </table>

      {/* ===== جمع‌ها (همه از invoice.totals) ===== */}
      {/* در ظرف راست‌به‌چپ، ms-auto بلوک را به سمت چپ می‌راند. */}
      <table className="mt-1 ms-auto text-[11px] border-collapse border border-black w-[45%]">
        <tbody>
          <tr>
            <td className={`${cell} bg-[#d9d9d9] font-bold w-1/2`}>جمع کل بدون مالیات بر ارزش افزوده</td>
            <td className={`${cell} text-left`}>{printAmount(totals.grand_total)}</td>
          </tr>
          <tr>
            <td className={cell}>بسته بندی 1%</td>
            <td className={`${cell} text-left`}>{printAmount(totals.packaging_cost)}</td>
          </tr>
          <tr>
            {/* <td className={cell}>مالیات برارزش افزوده 10%</td> */}
            <td className={cell}>مالیات بر ارزش افزوده 10%</td>
            <td className={`${cell} text-left`}>{printAmount(totals.vat_amount)}</td>
          </tr>
          <tr>
            <td className={`${cell} bg-[#d9d9d9] font-bold`}> مبلغ نهایی قابل پرداخت</td>
            <td className={`${cell} text-left font-bold`}>{printAmount(totals.final_amount)}</td>
          </tr>
        </tbody>
      </table>

      {/* ===== مبلغ به حروف ===== */}
      <div className="p-1 mt-1 text-[11px] border border-black">
        قابل پرداخت : مبلغ به حروف {numberToPersianWords(totals.final_amount)} ریال
      </div>

      {/* ===== توضیحات و QR ===== */}
      {/* چیدمان جدولی است چون در چاپ پایدارتر از flex رفتار می‌کند. */}
      <table className="w-full mt-1 border-collapse border border-black">
        <tbody>
          <tr>
            <td className="p-2 align-top text-[10px] leading-5">
              <b>توضیحات:</b>
              {/* هر رکورد یک بند مستقل است و شکست خط داخل بند حفظ می‌شود. */}
              <div className="mt-1">
                {descriptions.map((note) => (
                  <div key={note.id} className="whitespace-pre-line">
                    ✓ {note.description}
                  </div>
                ))}
              </div>

            </td>

            {/* QR سمت چپ و روبه‌روی توضیحات. محتوای آن نشانی «شرایط عمومی فروش»
                است تا با اسکن، گوشی مستقیم همان صفحه را باز کند (نه یک متن ساده).
                نشانی از خود سرور می‌آید (بدون اسلش پایانی) و withTrailingSlash آن را
                به شکل خواسته‌شده در مستند درمی‌آورد. */}
            <td className="p-2 align-top text-center w-[120px]">
              <QRCodeSVG
                value={saleTermsUrl(terms?.source_url)}
                size={96}
                level="M"
                marginSize={0}
              />
              <div className="mt-1 text-[8px] leading-3">شرایط عمومی فروش</div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ===== صفحه‌ی آخر: شرایط عمومی فروش ===== */}
      {/* break-before باعث می‌شود این بخش همیشه روی برگه‌ی جداگانه چاپ شود. */}
      {terms && (
        <div className="proforma-print__terms">
          <div className="pb-2 mb-3 text-center border-b border-black">
            <img src={logo} alt="ماهان پانل" className="inline-block h-12 align-middle" />
            <span className="inline-block mr-2 text-xl font-bold align-middle">ماهان پانل</span>
          </div>

          <h2 className="mb-3 text-base font-bold text-center">{show(terms.title)}</h2>

          {(terms.sections || []).map((section, i) => (
            <div key={i} className="mb-2">
              <b className="text-[11px]">{show(section.title)}</b>
              <ul className="mt-1 text-[10px] leading-5">
                {(section.items || []).map((line, j) => (
                  <li key={j}>• {line}</li>
                ))}
              </ul>
            </div>
          ))}

          {terms.contact && (
            <div className="pt-2 mt-3 text-[10px] leading-5 border-t border-black">
              {terms.contact.messenger_phone && (
                <div>
                  <b>شماره پیام‌رسان:</b> {ltrValue(terms.contact.messenger_phone)}
                </div>
              )}
              {terms.contact.office_address && <div>{terms.contact.office_address}</div>}
              {terms.contact.factory_address && <div>{terms.contact.factory_address}</div>}
              {(terms.contact.phone_numbers || []).length > 0 && (
                <div className="mt-1 text-center">
                  {ltrValue((terms.contact.phone_numbers || []).join(" | "))}
                </div>
              )}
              {terms.contact.website && (
                <div className="font-bold text-center">{ltrValue(terms.contact.website)}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProformaPrintDocument;
